<template>
  <div class="app">
    <!-- 头部 -->
    <header class="app-header">
      <div class="header-content">
        <div class="logo">
          <el-icon class="logo-icon"><Van /></el-icon>
          <div class="logo-text">
            <h1>SelfDriveTrip</h1>
            <p>智能自驾路线规划</p>
          </div>
        </div>
        <div class="header-stats" v-if="stats.routesCount > 0">
          <el-tag type="success" effect="dark">
            <el-icon><TrendCharts /></el-icon>
            已生成 {{ stats.routesCount }} 条路线
          </el-tag>
        </div>
      </div>
    </header>

    <!-- 主要内容区 -->
    <main class="main-content">
      <div class="content-wrapper">
        <!-- 左侧表单 -->
        <div class="form-section">
          <div class="section-card">
            <div class="card-header-title">
              <h2>🎯 规划您的自驾之旅</h2>
              <p>填写以下信息，AI将为您生成3条个性化路线</p>
            </div>
            
            <TripForm 
              :loading="generating"
              @submit="handleGenerate"
            />
          </div>

          <!-- 加载动画 -->
          <div v-if="generating" class="loading-section">
            <div class="loading-animation">
              <div class="car-icon">🚗</div>
              <p>AI正在为您规划路线...</p>
              <div class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <p class="loading-hint">正在分析从{{ formData.departureCity }}到{{ formData.destinationCity }}的自驾路线、计算往返距离、预估费用...</p>
            </div>
          </div>
        </div>

        <!-- 右侧结果 -->
        <div class="results-section" v-if="routes.length > 0">
          <div class="section-card">
            <div class="card-header-title results-header">
              <h2>🗺️ 为您生成的 {{ routes.length }} 条路线</h2>
              <el-button type="text" @click="clearRoutes">
                <el-icon><Delete /></el-icon>
                清除
              </el-button>
            </div>

            <div class="routes-grid">
              <RouteCard
                v-for="route in routes"
                :key="route.id"
                :route="route"
                @click="selectRoute(route)"
                @view-detail="selectRoute(route)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 最新路线板块 -->
      <LatestRoutes ref="latestRoutesRef" />
    </main>

    <!-- 页脚 -->
    <footer class="app-footer">
      <div class="footer-content">
        <p>© 2026 SelfDriveTrip - AI驱动的自驾游路线规划平台</p>
        <div class="footer-links">
          <a href="#" @click.prevent="showAbout">关于我们</a>
          <a href="#" @click.prevent="showContact">联系方式</a>
          <a href="#" @click.prevent="showPrivacy">隐私政策</a>
        </div>
      </div>
    </footer>

    <!-- 路线详情弹窗 -->
    <MapModal
      v-model="modalVisible"
      :route="selectedRoute"
    />

    <!-- 消息提示 -->
    <el-backtop :visibility-height="100" />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { Van, TrendCharts, Delete } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import TripForm from './components/TripForm.vue'
import RouteCard from './components/RouteCard.vue'
import MapModal from './components/MapModal.vue'
import LatestRoutes from './components/LatestRoutes.vue'
import { generateRoutes } from './api'

const generating = ref(false)
const routes = ref([])
const modalVisible = ref(false)
const selectedRoute = ref({})
const formData = ref({})
const latestRoutesRef = ref(null)

const stats = reactive({
  routesCount: 0
})

onMounted(() => {
  // Load stats from localStorage
  const savedStats = localStorage.getItem('selfdrivetrip_stats')
  if (savedStats) {
    Object.assign(stats, JSON.parse(savedStats))
  }
})

const handleGenerate = async (form) => {
  formData.value = form
  generating.value = true
  routes.value = []

  try {
    const response = await generateRoutes(form)
    
    if (response.success && response.routes) {
      routes.value = response.routes
      
      // Update stats
      stats.routesCount += response.routes.length
      localStorage.setItem('selfdrivetrip_stats', JSON.stringify(stats))
      
      ElMessage.success(`成功生成 ${response.routes.length} 条路线！`)
      
      // Refresh latest routes
      if (latestRoutesRef.value) {
        setTimeout(() => {
          latestRoutesRef.value.refreshRoutes()
        }, 1000)
      }
    } else {
      throw new Error('生成路线失败')
    }
  } catch (error) {
    console.error('Generate error:', error)
    ElMessage.error(error.message || '生成路线失败，请重试')
  } finally {
    generating.value = false
  }
}

const selectRoute = (route) => {
  selectedRoute.value = route
  modalVisible.value = true
}

const clearRoutes = () => {
  routes.value = []
  ElMessage.info('已清除生成的路线')
}

const showAbout = () => {
  ElMessage.info('SelfDriveTrip - AI驱动的自驾游路线规划平台')
}

const showContact = () => {
  ElMessage.info('如有问题，请在项目中提交Issue')
}

const showPrivacy = () => {
  ElMessage.info('我们重视您的隐私，不会存储个人敏感信息')
}
</script>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background: linear-gradient(135deg, #00B5FF 0%, #0077FF 100%);
  padding: 20px 0;
  box-shadow: 0 4px 20px rgba(0, 181, 255, 0.3);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo-icon {
  font-size: 48px;
  color: white;
}

.logo-text h1 {
  margin: 0;
  color: white;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.logo-text p {
  margin: 5px 0 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.header-stats {
  display: flex;
  gap: 10px;
}

.main-content {
  flex: 1;
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 40px;
}

@media (max-width: 968px) {
  .content-wrapper {
    grid-template-columns: 1fr;
  }
}

.form-section,
.results-section {
  min-width: 0;
}

.section-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.card-header-title {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.card-header-title h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #333;
}

.card-header-title p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.loading-section {
  margin-top: 30px;
  text-align: center;
}

.loading-animation {
  padding: 40px;
}

.car-icon {
  font-size: 64px;
  margin-bottom: 20px;
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

.loading-animation p {
  font-size: 18px;
  color: #333;
  margin-bottom: 20px;
  font-weight: 500;
}

.loading-dots {
  display: inline-flex;
  gap: 8px;
  margin-bottom: 20px;
}

.loading-dots span {
  width: 12px;
  height: 12px;
  background: #00B5FF;
  border-radius: 50%;
  animation: pulse 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes pulse {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-hint {
  font-size: 14px !important;
  color: #999 !important;
}

.routes-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.app-footer {
  background: #2c3e50;
  color: white;
  padding: 30px 20px;
  margin-top: auto;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.footer-content p {
  margin: 0 0 15px 0;
  opacity: 0.8;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 30px;
}

.footer-links a {
  color: white;
  text-decoration: none;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.footer-links a:hover {
  opacity: 1;
}
</style>